import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errorHandler";

const ManageBlockModal = observer(
  ({ open, handleClose, block, blockStore }) => {
    const [moviesInBlock, setMoviesInBlock] = useState([]);

    useEffect(() => {
      if (open && block) {
        setMoviesInBlock([]);
        blockStore.getMoviesInBlock(block.block_id);
      }
    }, [open, block, blockStore]);

    useEffect(() => {
      if (blockStore.responseMoviesInBlock?.data) {
        setMoviesInBlock(blockStore.responseMoviesInBlock.data);
      }
    }, [blockStore.responseMoviesInBlock]);

    const handleRemoveFromBlock = async (recordId) => {
      if (!recordId || !block) return;
      if (!window.confirm("Удалить фильм из блока?")) return;

      try {
        await blockStore.removeMovieFromBlock(recordId, block.block_id);
        await blockStore.getMoviesInBlock(block.block_id);
        toast.success("Фильм удалён из блока");
      } catch (error) {
        toast.error(`❌ ${getErrorMessage(error, "Не удалось удалить фильм")}`);
      }
    };

    const handleMoveDown = async (recordId) => {
      if (!recordId || !block) return;

      try {
        await blockStore.moveMovieDown(recordId, block.block_id);
        await blockStore.getMoviesInBlock(block.block_id);
        toast.success("Фильм опущен вниз");
      } catch (error) {
        toast.error(
          `❌ ${getErrorMessage(error, "Не удалось опустить фильм")}`
        );
      }
    };

    const isCountValid = moviesInBlock.length >= 3 && moviesInBlock.length <= 5;
    const uniqueMovies = new Set(moviesInBlock.map((film) => film.movie_id));
    const hasNoDuplicates = uniqueMovies.size === moviesInBlock.length;
    const isBlockReady = isCountValid && hasNoDuplicates;

    if (!block) return null;

    return (
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 650,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            maxHeight: "90vh",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <Paper sx={{ p: 2, mb: 3, backgroundColor: "#E0F7FA" }}>
            <Typography variant="h6" align="center">
              {block.name}
            </Typography>
          </Paper>

          <Paper
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: isBlockReady ? "#d4edda" : "#fff3cd",
              border: `2px solid ${isBlockReady ? "#28a745" : "#ffc107"}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isBlockReady ? (
                <CheckCircleIcon sx={{ color: "#28a745" }} />
              ) : (
                <WarningIcon sx={{ color: "#ffc107" }} />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight="bold">
                  {isBlockReady
                    ? "✓ Блок готов к использованию"
                    : "⚠ Блок не готов"}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Фильмов в блоке: <strong>{moviesInBlock.length}</strong> из
                  3–5
                  {!isCountValid && moviesInBlock.length > 0 && (
                    <span style={{ color: "#d32f2f" }}>
                      {" "}
                      — требуется от 3 до 5 фильмов
                    </span>
                  )}
                </Typography>
                {!hasNoDuplicates && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#d32f2f", mt: 0.5 }}
                  >
                    ⚠ Обнаружены повторяющиеся фильмы!
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                {blockStore.loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : moviesInBlock.length > 0 ? (
                  moviesInBlock.map((film) => (
                    <TableRow
                      key={film.id}
                      sx={{ height: "20px", "& td, & th": { padding: "4px" } }}
                    >
                      <TableCell>
                        {film.title ||
                          film.movie?.title ||
                          `Фильм ${film.movie_id}`}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleMoveDown(film.id)}
                          title="Опустить вниз"
                        >
                          <ArrowDownward />
                        </IconButton>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleRemoveFromBlock(film.id)}
                          title="Удалить из блока"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      В блоке пока нет фильмов
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
            <Button variant="outlined" onClick={handleClose}>
              Закрыть
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
);

export default ManageBlockModal;
